import { Alert, Drawer } from '@mui/material';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Stack } from '@mui/system';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { Link, Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useGetCustomerKYXVerificationQuery, useGetTransactionsQuery } from '@/api/v1';
import { FullScreenLoader } from '@/components/molecules/FullScreenLoader';
import { ActivitySearchBar } from '@/components/organisms/ActivitySearchBar';
import { MainScreen } from '@/components/organisms/MainScreen';
import { TransactionsTable } from '@/components/organisms/TransactionsTable';
import { FF_DASHBOARD_CUSTOMER_BALANCES } from '@/data/featureFlags';
import { useIntegratorFeatureFlag } from '@/hooks/useFeatureFlags';
import { useTranslator } from '@/hooks/useTranslator';
import { useTypedSearchParams } from '@/hooks/useTypedSearchParams';
import { AiPriseResumeButton } from '@/Screens/Dashboard/Customers/AiPriseResumeButton';
import { CustomerBalanceSection } from '@/Screens/Dashboard/Customers/CustomerBalanceSection';
import { NoCustomerActivityResultsView } from '@/Screens/Dashboard/Customers/NoCustomerActivityResultsView';
import { NoCustomerActivityView } from '@/Screens/Dashboard/Customers/NoCustomerActivityView';
import { BeneficiaryType } from '@/types/customer';
import { VerificationResult, VerificationStatus } from '@/types/customerKYXVerification';
import { GetTransactionsFilters } from '@/types/getTransactionsFilters';
import { CUSTOMER_ACTIVITY_COLUMNS } from '@/types/transactionColumn';
import { CUSTOMER_ACTIVITY_FILTERS } from '@/types/transactionFilterField';
import { countTransactionFilters } from '@/utility/countTransactionFilters';

export const CustomerDetails: React.FC = (): React.ReactElement => {
  const { customerId = '', transactionId = '' } = useParams<{
    readonly customerId: string;
    readonly transactionId: string;
  }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const tr = useTranslator();
  const [filters, setFilters] = useTypedSearchParams<GetTransactionsFilters>(
    GetTransactionsFilters.initial(),
  );

  const {
    data: customer,
    isLoading,
    isError,
  } = useGetCustomerKYXVerificationQuery(customerId, { skip: !customerId });
  const isDashboardCustomerBalancesFeatureFlagEnabled = useIntegratorFeatureFlag(
    FF_DASHBOARD_CUSTOMER_BALANCES,
  );
  const title = tr('CUSTOMERS');

  const onRowClick = React.useCallback(
    (transactionId: string): void => {
      navigate({
        pathname: `activity/${transactionId}`,
        search: search.toString(),
      });
    },
    [navigate, search],
  );

  const closeTransactionDetails = React.useCallback((): void => {
    navigate({
      pathname: '.',
      search: search.toString(),
    });
  }, [navigate, search]);

  const handleFiltersChange = React.useCallback(
    (newFilters: GetTransactionsFilters): void => {
      setFilters(newFilters);
    },
    [setFilters],
  );

  const filtersCount = React.useMemo((): number => countTransactionFilters(filters), [filters]);

  const queryFilters = React.useMemo(
    (): Partial<GetTransactionsFilters> => ({
      ...filters,
      beneficiaryId: customer?.beneficiaryId ?? undefined,
    }),
    [filters, customer?.beneficiaryId],
  );

  const { data: transactionsPage } = useGetTransactionsQuery(queryFilters, {
    skip: !customer?.beneficiaryId,
  });

  const hasNoTransactions = !transactionsPage || transactionsPage.content.length === 0;
  // No activity at all (no filters applied, no data) - hide filter/download buttons
  const hasNoContent = hasNoTransactions && filtersCount === 0;

  if (isLoading) {
    return <FullScreenLoader loading />;
  }

  if (isError || !customer) {
    return (
      <MainScreen title={title}>
        <Alert severity="error">{tr('ERROR_LOADING_CUSTOMER')}</Alert>
      </MainScreen>
    );
  }

  const customerName: string | null =
    customer.type === BeneficiaryType.business
      ? customer.businessName
      : customer.type === BeneficiaryType.individual
        ? `${customer.firstName} ${customer.lastName}`
        : null;

  const isVerificationNotStarted: boolean =
    customer.verificationStatus === VerificationStatus.NOT_STARTED;

  const isReadyToTransact: boolean =
    customer.verificationResult === VerificationResult.APPROVED && !!customer.beneficiaryId;

  const breadcrumbs: React.ReactElement = (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography
        component={Link}
        to="/customers"
        variant="h6"
        color="text.secondary"
        sx={styles.title}>
        {title}
      </Typography>
      <Typography variant="h6" color="text.secondary">
        {'>'}
      </Typography>
      {customerName && <Typography variant="h6">{customerName}</Typography>}
    </Stack>
  );

  return (
    <MainScreen title={breadcrumbs}>
      <Stack gap={4} sx={styles.container}>
        <Paper elevation={3} sx={styles.paper}>
          <Stack gap={4}>
            <Stack gap={2}>
              <Stack direction="row" gap={3} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {tr('NAME')}
                </Typography>
                <Typography variant="body2">{customerName}</Typography>
              </Stack>

              <Stack direction="row" gap={3} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {tr('CUSTOMER_TYPE')}
                </Typography>
                <Typography variant="body2">{tr(customer.type)}</Typography>
              </Stack>
            </Stack>

            <Divider />

            <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between">
              <Stack gap={1} flex={1}>
                <Typography variant="h7">{tr('VERIFICATION_STATUS')}</Typography>

                {isVerificationNotStarted && (
                  <Typography variant="body2" color="text.secondary">
                    {tr('CUSTOMER_VERIFICATION_INFO')}
                  </Typography>
                )}

                <Stack direction="row" gap={0.5} mt={0.5}>
                  <Typography variant="body1" color="text.secondary">
                    {tr('STATUS')}:
                  </Typography>
                  <Typography variant="body1">{tr(customer.verificationStatus)}</Typography>
                </Stack>

                <Stack direction="row" gap={0.5}>
                  <Typography variant="body1" color="text.secondary">
                    {tr('RESULT')}:
                  </Typography>
                  <Typography variant="body1">{tr(customer.verificationResult)}</Typography>
                </Stack>
              </Stack>
              {isVerificationNotStarted && <AiPriseResumeButton />}
            </Stack>
          </Stack>
        </Paper>
        {isDashboardCustomerBalancesFeatureFlagEnabled &&
          isReadyToTransact &&
          customer.beneficiaryId && (
            <CustomerBalanceSection
              beneficiaryId={customer.beneficiaryId}
              beneficiaryType={customer.type}
              transferLink={`/customers/${customerId}/transfer`}
              depositLink={`/customers/${customerId}/deposit`}
            />
          )}

        <Paper elevation={3} sx={styles.activityPaper}>
          <Stack gap={4}>
            <Typography variant="h7">{tr('ACTIVITY')}</Typography>
            {isReadyToTransact ? (
              <>
                {!hasNoContent && (
                  <ActivitySearchBar
                    filtersCount={filtersCount}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    beneficiaryId={customer.beneficiaryId}
                    visibleFilters={CUSTOMER_ACTIVITY_FILTERS}
                    isDownloadDisabled={hasNoTransactions}
                  />
                )}
                <TransactionsTable
                  beneficiaryId={customer.beneficiaryId}
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onRowClick={onRowClick}
                  noContentView={
                    <NoCustomerActivityView
                      verificationResult={customer.verificationResult}
                      beneficiaryId={customer.beneficiaryId}
                    />
                  }
                  noResultsView={<NoCustomerActivityResultsView />}
                  visibleColumns={CUSTOMER_ACTIVITY_COLUMNS}
                />
              </>
            ) : (
              <NoCustomerActivityView
                verificationResult={customer.verificationResult}
                beneficiaryId={customer.beneficiaryId}
              />
            )}
          </Stack>
        </Paper>
      </Stack>

      {transactionId ? (
        <Drawer open anchor="right" onClose={closeTransactionDetails}>
          <Outlet />
        </Drawer>
      ) : (
        <Outlet />
      )}
    </MainScreen>
  );
};

const styles: Record<string, SystemStyleObject> = {
  title: {
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  container: {
    pb: 2,
  },
  paper: {
    p: 5,
    borderRadius: 2,
  },
  activityPaper: {
    px: 4,
    py: 3,
    borderRadius: 2,
  },
};
